import { IBaseTyreOffer } from '~/purchase/types';
import { Col, Flex, Icon, Row, Text, Tooltip, TyreInfoBox } from '@wyz/ui';
import { mapTyreIntoInfoBox, useBoolean } from '@wyz/utils';
import clsx from 'clsx';
import * as React from 'react';
import { Trans, useConfig, useI18n, useLocale } from '~/common/context';
import { IUserCustomer } from '~/common/types';
import {
  PurchaseDetailModal,
  PurchaseOfferDescriptionCell,
} from '~/purchase/components';

type Props = {
  showCommon: boolean;
  customer: IUserCustomer;
  product: Pick<
    IBaseTyreOffer,
    | 'status'
    | 'totalAvailableQuantity'
    | 'tyre'
    | 'tyreCreatedAt'
    | 'alternativeCount'
    | 'canOrder'
    | 'customerStock'
    | 'proximityStocks'
  >;
  showStockDetails: boolean;
  toggleShowStockDetails: () => void;
};

const ErrorComponent = ({
  status,
  text,
}: {
  status: Props['product']['status'];
  text?: boolean;
}) => {
  const ICON: Partial<Record<typeof status, React.ReactNode>> = {
    TECHNICAL_ERROR: (
      <Tooltip
        label={
          <Trans i18nKey='app.purchase.cart_page.unavailable_modal.unavailable_product' />
        }
      >
        <Flex justifyContent='center'>
          <Icon icon='alert' size='sm' />
        </Flex>
      </Tooltip>
    ),
    EMPTY_STOCK: (
      <Tooltip
        label={
          <Trans i18nKey='app.purchase.cart_page.unavailable_modal.unavailable_product' />
        }
      >
        <Flex justifyContent='center'>
          <Icon icon='alert' size='sm' />
        </Flex>
      </Tooltip>
    ),
  };
  const TEXT: Partial<Record<typeof status, React.ReactNode>> = {
    TECHNICAL_ERROR: (
      <Text
        heading='xs'
        weight='semi-bold'
        color='secondary-gray'
        className='text-wrap px-2 text-center'
      >
        <Trans i18nKey='app.purchase.cart_page.unavailable_modal.technical' />
      </Text>
    ),
    EMPTY_STOCK: (
      <Text
        heading='xs'
        weight='semi-bold'
        color='secondary-gray'
        className='text-wrap px-2 text-center'
      >
        <Trans i18nKey='app.purchase.cart_page.unavailable_modal.unavailable_product' />
      </Text>
    ),
  };
  if (!text) {
    return <>{ICON[status]}</>;
  }
  return <>{TEXT[status]}</>;
};

function OfferErrorRow({
  product,
  showCommon,
  customer,
  showStockDetails,
  toggleShowStockDetails,
}: Props) {
  const [detailOpened, detailHandlers] = useBoolean();
  const { t } = useI18n();
  const { cdnUrl } = useConfig();
  const locale = useLocale();

  const hasLocalOrProximityStock =
    (product.customerStock ?? 0) > 0 || product.proximityStocks.length > 0;

  return (
    <tr
      className={clsx('text-nowrap fs-xs hasError position-relative', {
        subhead: showCommon,
      })}
    >
      {showCommon && (
        <td id='product' rowSpan={1}>
          <TyreInfoBox
            imageBaseUrl={cdnUrl}
            t={t}
            tyre={mapTyreIntoInfoBox(
              product.tyre,
              customer.settingTemplate?.generalSetting.tyreIdentifierFields ??
                undefined,
            )}
            direction='horizontal'
            mode='FULL'
          />
        </td>
      )}
      {showCommon && (
        <td id='detail' rowSpan={1} className='p-0'>
          <PurchaseOfferDescriptionCell
            tyre={product.tyre}
            onDetail={detailHandlers.setTrue}
          />
        </td>
      )}
      {showCommon && (
        <td id='bf-price' rowSpan={1} className='text-center align-middle'>
          <Text heading='xs' weight='semi-bold'>
            {locale.formatters.money(product.tyre.priceFrame.price, {
              currency: product.tyre.priceFrame.currencyIsoCode ?? undefined,
              fallback: '/',
            })}
          </Text>
        </td>
      )}

      {showCommon &&
        (customer?.hasInternalStocks || customer?.hasProximityStocks) && (
          <td
            className='text-center align-middle'
            rowSpan={1}
            id='customer-stock'
          >
            {hasLocalOrProximityStock ? (
              <>
                <Text heading='xs' weight='semi-bold'>
                  {product.customerStock ?? '/'}
                </Text>
                <Icon
                  id={`stock-trigger-${product.tyre.ean}`}
                  icon='info-circle'
                  className='cursor-pointer mx-auto mt-2'
                  onClick={toggleShowStockDetails}
                />
              </>
            ) : (
              <Text heading='xs' weight='semi-bold'>
                {'/'}
              </Text>
            )}
          </td>
        )}
      {showStockDetails && showCommon && (
        <td
          id='stock-details'
          data-cy={`stock-details-panel-${product.tyre.ean}`}
          colSpan={5}
          rowSpan={1}
        >
          <div className='py-1 px-3'>
            <Flex alignItems='center' justifyContent='between'>
              <Text
                heading='normal'
                weight='semi-bold'
                color='tertiary'
                id={`stock-details-title-${product.tyre.ean}`}
              >
                <Trans i18nKey='app.purchase.table.stock_details.title' />
              </Text>
              <div
                className='rounded rounded-circle bg-light p-2 cursor-pointer border border-1'
                data-cy={`stock-details-close-${product.tyre.ean}`}
                onClick={toggleShowStockDetails}
              >
                <Icon icon='close' />
              </div>
            </Flex>
            <Row>
              <Col>
                <Text
                  heading='sm'
                  color='primary'
                  weight='semi-bold'
                  id={`stock-details-local-label-${product.tyre.ean}`}
                >
                  <Trans i18nKey='app.purchase.table.stock_details.local' />
                  {' :'}
                </Text>
              </Col>
              <Col>
                <Text
                  heading='sm'
                  color='primary'
                  weight='semi-bold'
                  id={`stock-details-local-value-${product.tyre.ean}`}
                >
                  {product.customerStock ?? 0}
                </Text>
              </Col>
            </Row>
            {product.proximityStocks.map((stock, stockIndex) => (
              <Row
                key={stockIndex}
                data-cy={`stock-details-proximity-row-${product.tyre.ean}-${stockIndex}`}
              >
                <Col>
                  <Text
                    heading='sm'
                    color='primary'
                    weight='semi-bold'
                    id={`stock-details-proximity-supplier-${product.tyre.ean}-${stockIndex}`}
                  >
                    {stock.supplierName}
                    {' :'}
                  </Text>
                </Col>
                <Col>
                  <Text
                    heading='sm'
                    color='primary'
                    weight='semi-bold'
                    id={`stock-details-proximity-quantity-${product.tyre.ean}-${stockIndex}`}
                  >
                    {stock.quantity}
                  </Text>
                </Col>
              </Row>
            ))}
          </div>
          <div
            className='stock-overlay-left rounded'
            onClick={toggleShowStockDetails}
          />
          <div
            className='stock-overlay-right rounded'
            onClick={toggleShowStockDetails}
          />
        </td>
      )}

      {!showStockDetails && (
        <>
          <td id='supplier' className='p-0 no-border'>
            <Flex
              direction='column'
              justifyContent='around'
              className='list-unstyled h-100 w-100'
              wrap='wrap'
            >
              <ErrorComponent status={product.status} />
            </Flex>
          </td>

          <td id='delivery' className='p-0 no-border'>
            <Flex
              direction='column'
              alignItems='center'
              justifyContent='around'
              className='list-unstyled h-100 w-100'
              wrap='wrap'
            >
              <ErrorComponent status={product.status} />
            </Flex>
          </td>

          <td id='stock' className='p-0 no-border'>
            <Flex
              direction='column'
              alignItems='center'
              justifyContent='center'
              className='list-unstyled h-100 w-100'
              wrap='wrap'
            >
              <ErrorComponent status={product.status} />
            </Flex>
          </td>
          <td id='quantity' className='p-0 no-border'>
            <div>
              <ErrorComponent status={product.status} />
            </div>
          </td>
          <td id='unit-price' className='p-0 no-border' align='right'>
            <Flex
              direction='column'
              justifyContent='around'
              className='w-100 h-100 '
              wrap='wrap'
            >
              <ErrorComponent status={product.status} />
            </Flex>
          </td>
        </>
      )}
      {showCommon && (
        <td id='action' rowSpan={1} className='align-middle p-0 '>
          <Flex
            className={clsx('p-1', {
              // 'space-y-2': !hasError,
            })}
            direction='column'
            justifyContent='center'
            alignItems='center'
          >
            <ErrorComponent status={product.status} text />
          </Flex>
        </td>
      )}
      {detailOpened && (
        <PurchaseDetailModal
          product={product.tyre}
          onClose={detailHandlers.setFalse}
          customer={customer}
        />
      )}
    </tr>
  );
}

export default OfferErrorRow;
